/**
 * 图生图功能页面
 */

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { 
  Upload, 
  Image as ImageIcon, 
  Wand2, 
  Download, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Coins,
  CreditCard
} from 'lucide-react'
import { imageGenerationService } from '@/services/imageGenerationService'
import { creditService } from '@/services/creditService'
import { useAuth } from '@/contexts/AuthContext'
import { 
  ImageGenerationState, 
  ImageGenerationRequest,
  ImageGenerationHistory 
} from '@/types/imageGeneration'

export default function ImageGenerationPage() {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { user } = useAuth()
  
  const [state, setState] = useState<ImageGenerationState>({
    isGenerating: false,
    uploadedImage: null,
    userPrompt: '',
    generatedImageUrl: null,
    error: null,
    progress: 0
  })

  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null)
  const [userCredits, setUserCredits] = useState<number>(0)
  const [creditsLoading, setCreditsLoading] = useState<boolean>(true)

  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [history, setHistory] = useState<ImageGenerationHistory[]>([])

  // 积分消耗配置
  const CREDIT_COST = 10

  // 默认提示词
  const defaultPrompt = imageGenerationService.getDefaultConfig().prompt
  const fullPrompt = state.userPrompt 
    ? `${defaultPrompt}, ${state.userPrompt}` 
    : defaultPrompt

  /**
   * 获取用户积分余额
   */
  const fetchUserCredits = useCallback(async () => {
    if (!user) {
      setUserCredits(0)
      setCreditsLoading(false)
      return
    }

    try {
      setCreditsLoading(true)
      const credits = await creditService.getUserCredits(user.id)
      setUserCredits(credits?.credits || 0)
    } catch (error) {
      console.error('Error fetching user credits:', error)
      setUserCredits(0)
    } finally {
      setCreditsLoading(false)
    }
  }, [user])

  /**
   * 检查用户是否有足够积分
   */
  const hasEnoughCredits = useCallback(() => {
    return userCredits >= CREDIT_COST
  }, [userCredits, CREDIT_COST])

  // 组件挂载时获取用户积分
  useEffect(() => {
    fetchUserCredits()
  }, [fetchUserCredits])

  // 当用户登录状态变化时重新获取积分
  useEffect(() => {
    if (user) {
      fetchUserCredits()
    } else {
      setUserCredits(0)
      setCreditsLoading(false)
    }
  }, [user, fetchUserCredits])

  /**
   * 处理图片上传
   */
  const handleImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 验证文件
    const validation = imageGenerationService.validateImageFile(file)
    if (!validation.valid) {
      setState(prev => ({ ...prev, error: validation.error || '文件验证失败' }))
      return
    }

    // 清理之前的预览
    if (imagePreview) {
      imageGenerationService.revokeImagePreview(imagePreview)
    }

    // 创建新的预览
    const preview = imageGenerationService.createImagePreview(file)
    setImagePreview(preview)
    setState(prev => ({ 
      ...prev, 
      uploadedImage: file, 
      error: null,
      generatedImageUrl: null 
    }))
  }, [imagePreview])

  /**
   * 处理提示词输入
   */
  const handlePromptChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setState(prev => ({ ...prev, userPrompt: event.target.value }))
  }, [])

  /**
   * 生成图片
   */
  const handleGenerate = useCallback(async () => {
    if (!state.uploadedImage) {
      setState(prev => ({ ...prev, error: t('imageGeneration.uploadImageFirst') }))
      return
    }

    // 检查用户是否登录
    if (!user) {
      setState(prev => ({ ...prev, error: t('imageGeneration.loginRequired') }))
      return
    }

    // 检查积分是否足够
    if (!hasEnoughCredits()) {
      setState(prev => ({ 
        ...prev, 
        error: t('imageGeneration.insufficientCreditsError', { credits: CREDIT_COST, current: userCredits })
      }))
      return
    }

    setState(prev => ({ 
      ...prev, 
      isGenerating: true, 
      error: null, 
      progress: 0 
    }))

    // 模拟进度更新
    const progressInterval = setInterval(() => {
      setState(prev => ({
        ...prev,
        progress: Math.min(prev.progress + Math.random() * 20, 90)
      }))
    }, 500)

    try {
      // 先扣除积分
      const creditResult = await creditService.consumeCredits(
        user.id,
        CREDIT_COST,
        'AI图片生成',
        undefined,
        'image_generation'
      )

      if (!creditResult.success) {
        clearInterval(progressInterval)
        setState(prev => ({ 
          ...prev, 
          error: creditResult.error || t('imageGeneration.creditDeductionFailed'),
          isGenerating: false 
        }))
        return
      }

      // 更新本地积分显示
      setUserCredits(creditResult.newBalance || userCredits - CREDIT_COST)

      const request: ImageGenerationRequest = {
        image: state.uploadedImage,
        prompt: fullPrompt
      }

      const response = await imageGenerationService.generateImage(request)

      clearInterval(progressInterval)
      setState(prev => ({ ...prev, progress: 100 }))

      if (response.success) {
        // 检查是否有生成的图片URL
        if (response.imageUrl) {
          setState(prev => ({ 
            ...prev, 
            generatedImageUrl: response.imageUrl!,
            isGenerating: false 
          }))

          // 添加到历史记录
          const historyItem: ImageGenerationHistory = {
            id: Date.now().toString(),
            originalImageUrl: imagePreview!,
            prompt: fullPrompt,
            generatedImageUrl: response.imageUrl!,
            createdAt: new Date(),
            processingTime: response.processingTime || 0
          }
          setHistory(prev => [historyItem, ...prev])
        } else if (response.textResponse) {
          // 如果API返回的是文本描述，显示给用户
          setAiAnalysis(response.textResponse)
          setState(prev => ({ 
            ...prev, 
            isGenerating: false 
          }))
        } else {
          setState(prev => ({ 
            ...prev, 
            error: t('imageGeneration.apiSuccessNoImage'),
            isGenerating: false 
          }))
        }
      } else {
        setState(prev => ({ 
          ...prev, 
          error: response.error || t('imageGeneration.generationFailed'),
          isGenerating: false 
        }))
      }
    } catch (error) {
      clearInterval(progressInterval)
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : t('imageGeneration.unknownError'),
        isGenerating: false 
      }))
    }
  }, [state.uploadedImage, fullPrompt, imagePreview, user, hasEnoughCredits, userCredits, CREDIT_COST])

  /**
   * 重置状态
   */
  const handleReset = useCallback(() => {
    if (imagePreview) {
      imageGenerationService.revokeImagePreview(imagePreview)
    }
    setImagePreview(null)
    setAiAnalysis(null)
    setState({
      isGenerating: false,
      uploadedImage: null,
      userPrompt: '',
      generatedImageUrl: null,
      error: null,
      progress: 0
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [imagePreview])

  /**
   * 下载生成的图片
   */
  const handleDownload = useCallback(() => {
    if (!state.generatedImageUrl) return

    const link = document.createElement('a')
    link.href = state.generatedImageUrl
    link.download = `generated-image-${Date.now()}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [state.generatedImageUrl])

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{t('imageGeneration.title')}</h1>
        <p className="text-muted-foreground">
          {t('imageGeneration.subtitle')}
        </p>
        <div className="mt-2">
          <Badge variant="outline" className="text-xs">
            {t('imageGeneration.model')}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 左侧：输入区域 */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                {t('imageGeneration.uploadImage')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* 图片上传区域 */}
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                {imagePreview ? (
                  <div className="space-y-4">
                    <img 
                      src={imagePreview} 
                      alt="上传的图片" 
                      className="max-w-full max-h-64 mx-auto rounded-lg object-contain"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      {t('imageGeneration.reselectImage')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {t('imageGeneration.clickOrDrag')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('imageGeneration.supportedFormats')}
                      </p>
                    </div>
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {t('imageGeneration.selectImage')}
                    </Button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>

              {/* 提示词输入 */}
              <div className="space-y-2">
                <Label htmlFor="prompt">{t('imageGeneration.prompt')}</Label>
                <Textarea
                  id="prompt"
                  placeholder={t('imageGeneration.promptPlaceholder')}
                  value={state.userPrompt}
                  onChange={handlePromptChange}
                  rows={4}
                  className="resize-none"
                />
                <div className="text-xs text-muted-foreground">
                  <p>{t('imageGeneration.fullPrompt')}：</p>
                  <Badge variant="secondary" className="text-xs">
                    {fullPrompt}
                  </Badge>
                </div>
              </div>

              {/* 生成按钮 */}
              <Button 
                onClick={handleGenerate}
                disabled={!state.uploadedImage || state.isGenerating || !user || !hasEnoughCredits()}
                className="w-full"
                size="lg"
              >
                {state.isGenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t('imageGeneration.processing')}
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    {t('imageGeneration.processImage')}
                  </>
                )}
              </Button>

              {/* 重置按钮 */}
              <Button 
                variant="outline" 
                onClick={handleReset}
                className="w-full"
              >
                {t('imageGeneration.reset')}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* 右侧：结果展示区域 */}
        <div className="space-y-6">
          {/* 积分信息卡片 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5" />
                {t('imageGeneration.creditInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* 当前积分余额 */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('imageGeneration.currentBalance')}</span>
                  <div className="flex items-center gap-2">
                    {creditsLoading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <span className="text-lg font-bold text-blue-600">
                        {userCredits} {t('imageGeneration.credits')}
                      </span>
                    )}
                  </div>
                </div>

                {/* 本次消耗 */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('imageGeneration.thisConsumption')}</span>
                  <span className="text-lg font-bold text-red-600">
                    -{CREDIT_COST} {t('imageGeneration.credits')}
                  </span>
                </div>

                {/* 积分状态 */}
                <div className="pt-2 border-t">
                  {!user ? (
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {t('imageGeneration.pleaseLoginFirst')}
                      </AlertDescription>
                    </Alert>
                  ) : !hasEnoughCredits() ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {t('imageGeneration.insufficientCredits', { credits: CREDIT_COST })}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">{t('imageGeneration.sufficientCredits')}</span>
                    </div>
                  )}
                </div>

                {/* 购买积分按钮 */}
                {user && !hasEnoughCredits() && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open('/pricing', '_blank')}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t('imageGeneration.buyCredits')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                {t('imageGeneration.generationResults')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* 进度条 */}
              {state.isGenerating && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span>{t('imageGeneration.generationProgress')}</span>
                    <span>{Math.round(state.progress)}%</span>
                  </div>
                  <Progress value={state.progress} className="w-full" />
                </div>
              )}

              {/* 错误提示 */}
              {state.error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{state.error}</AlertDescription>
                </Alert>
              )}

              {/* 生成结果 */}
              {state.generatedImageUrl ? (
                <div className="space-y-4">
                  <div className="relative">
                    <img 
                      src={state.generatedImageUrl} 
                      alt={t('imageGeneration.generatedImage')} 
                      className="w-full rounded-lg border"
                    />
                    <div className="absolute top-2 right-2">
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {t('imageGeneration.generationComplete')}
                      </Badge>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={handleDownload}
                    className="w-full"
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('imageGeneration.downloadImage')}
                  </Button>
                </div>
              ) : aiAnalysis ? (
                <div className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-medium">{t('imageGeneration.aiGenerationResult')}：</p>
                        <p className="text-sm whitespace-pre-wrap">{aiAnalysis}</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      {t('imageGeneration.textAnalysisNote')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
                  <ImageIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {state.isGenerating ? t('imageGeneration.processingImage') : t('imageGeneration.resultWillShowHere')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 历史记录 */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('imageGeneration.generationHistory')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {history.map((item) => (
                    <div key={item.id} className="flex gap-4 p-3 border rounded-lg">
                      <div className="flex-shrink-0">
                        <img 
                          src={item.originalImageUrl} 
                          alt={t('imageGeneration.originalImage')} 
                          className="w-16 h-16 object-cover rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.prompt}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.createdAt.toLocaleString()} · {item.processingTime}ms
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <img 
                          src={item.generatedImageUrl} 
                          alt={t('imageGeneration.generatedImage')} 
                          className="w-16 h-16 object-cover rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
